<script>
/* global axios */
// Shows WAHA session status on the inbox settings page and provides a
// "reconectar" button that restarts the session and opens a QR code modal.
//
// Also exposes two entry points to the history import flow (wa/messages pull):
// - an "Importar histórico" button while connected (opt-A, permanent)
// - an auto-prompt right after a successful QR reconnect when the inbox
//   has never had a history import before (opt-B, the nudge)
//
// Only visible on inboxes that already went through the WAHA bootstrap
// (Settings.vue gates this via isWahaInbox computed).
import WahaHistoryImportModal from './WahaHistoryImportModal.vue';

const PASSKEY_REQUEST_CANCELLED = 'PASSKEY_REQUEST_CANCELLED';

const STATUS_META = {
  WORKING: {
    labelKey: 'INBOX_MGMT.WAHA_STATUS.STATUS.WORKING',
    color: 'bg-green-500',
    textColor: 'text-green-600 dark:text-green-400',
    icon: 'i-ri-checkbox-circle-line',
  },
  SCAN_QR_CODE: {
    labelKey: 'INBOX_MGMT.WAHA_STATUS.STATUS.SCAN_QR_CODE',
    color: 'bg-amber-500',
    textColor: 'text-amber-600 dark:text-amber-400',
    icon: 'i-ri-qr-code-line',
  },
  PASSKEY_REQUIRED: {
    labelKey: 'INBOX_MGMT.WAHA_STATUS.STATUS.PASSKEY_REQUIRED',
    color: 'bg-violet-500',
    textColor: 'text-violet-600 dark:text-violet-400',
    icon: 'i-ri-key-2-line',
  },
  PASSKEY_CONFIRMATION_REQUIRED: {
    labelKey: 'INBOX_MGMT.WAHA_STATUS.STATUS.PASSKEY_CONFIRMATION_REQUIRED',
    color: 'bg-violet-500',
    textColor: 'text-violet-600 dark:text-violet-400',
    icon: 'i-ri-smartphone-line',
  },
  STARTING: {
    labelKey: 'INBOX_MGMT.WAHA_STATUS.STATUS.STARTING',
    color: 'bg-blue-500',
    textColor: 'text-blue-600 dark:text-blue-400',
    icon: 'i-ri-loader-4-line',
  },
  STOPPED: {
    labelKey: 'INBOX_MGMT.WAHA_STATUS.STATUS.STOPPED',
    color: 'bg-n-slate-8',
    textColor: 'text-n-slate-11',
    icon: 'i-ri-pause-circle-line',
  },
  FAILED: {
    labelKey: 'INBOX_MGMT.WAHA_STATUS.STATUS.FAILED',
    color: 'bg-red-500',
    textColor: 'text-red-600 dark:text-red-400',
    icon: 'i-ri-close-circle-line',
  },
  NOT_FOUND: {
    labelKey: 'INBOX_MGMT.WAHA_STATUS.STATUS.NOT_FOUND',
    color: 'bg-red-500',
    textColor: 'text-red-600 dark:text-red-400',
    icon: 'i-ri-question-line',
  },
  ERROR: {
    labelKey: 'INBOX_MGMT.WAHA_STATUS.STATUS.ERROR',
    color: 'bg-red-500',
    textColor: 'text-red-600 dark:text-red-400',
    icon: 'i-ri-alert-line',
  },
  UNKNOWN: {
    labelKey: 'INBOX_MGMT.WAHA_STATUS.STATUS.UNKNOWN',
    color: 'bg-n-slate-8',
    textColor: 'text-n-slate-11',
    icon: 'i-ri-question-line',
  },
};

export default {
  name: 'WahaStatusCard',

  components: { WahaHistoryImportModal },

  props: {
    inbox: { type: Object, required: true },
  },

  data() {
    return {
      status: null,
      sessionName: null,
      engine: null,
      checkedAt: null,
      loading: false,
      reconnecting: false,
      showQrModal: false,
      qrCode: null,
      qrError: null,
      qrPollTimer: null,
      passkeyBusy: false,
      passkeyError: null,
      passkeyCode: null,
      passkeyModalDismissed: false,
      passkeySignCleanup: null,
      isUnmounted: false,
      statusRequestSequence: 0,
      qrRequestSequence: 0,
      extensionReady: false,
      statusPollTimer: null,
      showHistoryImportModal: false,
      historyImportedAt: null,
      historyImportCommand: null,
      // Tracks whether we were reconnecting when WORKING was reached so we
      // can fire the post-scan nudge only after a fresh connection.
      reconnectInProgress: false,
      // "Configurações WAHA" sub-card — toggles that re-register the plugin
      // app via PATCH /waha/settings so the plugin stays in sync with the
      // admin's intent.
      showSettings: false,
      settingsLoaded: false,
      settingsSaving: false,
      settingsSavedAt: null,
      settingsError: null,
      settings: {
        show_agent_name: true,
        mark_as_read: true,
        enable_commands: true,
        locale: 'pt-BR',
      },
    };
  },

  computed: {
    accountId() {
      return this.$route.params.accountId;
    },

    meta() {
      const meta = STATUS_META[this.status] || STATUS_META.UNKNOWN;
      // Status values come from WAHA, so their allowlisted translation keys are selected dynamically.
      // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
      return { ...meta, label: this.$t(meta.labelKey) };
    },

    showReconnect() {
      return !['WORKING', 'STARTING'].includes(this.status);
    },

    showImportHistory() {
      return this.status === 'WORKING';
    },

    showContinuePasskey() {
      return (
        !this.showQrModal &&
        (this.status === 'PASSKEY_REQUIRED' ||
          this.status === 'PASSKEY_CONFIRMATION_REQUIRED')
      );
    },

    passkeyExtensionStoreUrl() {
      if (this.isFirefoxBrowser()) {
        return 'https://addons.mozilla.org/firefox/addon/whatsapp-browser-extension';
      }
      return 'https://chromewebstore.google.com/detail/ghpdcgnjffaaekflfpcgkgpbafmjldcp';
    },

    historyImportedLabel() {
      if (!this.historyImportedAt) return null;
      try {
        return new Date(this.historyImportedAt).toLocaleString('pt-BR');
      } catch (e) {
        return this.historyImportedAt;
      }
    },

    checkedAtLabel() {
      if (!this.checkedAt) return '—';
      try {
        return new Date(this.checkedAt).toLocaleString('pt-BR');
      } catch (e) {
        return this.checkedAt;
      }
    },
  },

  mounted() {
    // Pull initial state from inbox.additional_attributes, then fetch fresh
    const attrs = this.inbox.additional_attributes || {};
    this.status = attrs.waha_status || 'UNKNOWN';
    this.sessionName = attrs.waha_session_name || '—';
    this.engine = attrs.waha_engine || 'GOWS';
    this.checkedAt = attrs.waha_status_checked_at;
    this.historyImportedAt = attrs.waha_history_imported_at;
    this.historyImportCommand = attrs.waha_history_import_command;

    this.pingPasskeyExtension();
    this.fetchStatus();
    this.fetchHistoryImportStatus();
    this.startStatusPolling();
  },

  beforeUnmount() {
    this.isUnmounted = true;
    this.stopStatusPolling();
    this.stopQrPolling();
    if (this.passkeySignCleanup) {
      this.passkeySignCleanup();
      this.passkeySignCleanup = null;
    }
  },

  methods: {
    async fetchStatus() {
      if (this.passkeyBusy) return;
      const requestSequence = (this.statusRequestSequence ?? 0) + 1;
      this.statusRequestSequence = requestSequence;
      this.loading = true;
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/inboxes/${this.inbox.id}/waha/status`
        );
        if (
          this.isUnmounted ||
          this.passkeyBusy ||
          requestSequence !== this.statusRequestSequence
        )
          return;
        this.qrRequestSequence = (this.qrRequestSequence ?? 0) + 1;
        this.status = data.status || 'UNKNOWN';
        this.sessionName = data.session_name || this.sessionName;
        this.engine = data.engine || this.engine;
        this.checkedAt = data.checked_at;
        if (
          data.status === 'PASSKEY_REQUIRED' ||
          data.status === 'PASSKEY_CONFIRMATION_REQUIRED'
        ) {
          this.stopQrPolling();
          this.qrCode = null;
          this.qrError = null;
          if (!this.passkeyModalDismissed) {
            this.showQrModal = true;
            await this.pingPasskeyExtension();
            if (
              this.isUnmounted ||
              this.passkeyBusy ||
              requestSequence !== this.statusRequestSequence
            )
              return;
            if (data.status === 'PASSKEY_CONFIRMATION_REQUIRED') {
              await this.fetchPasskeyConfirmation();
            }
          }
        } else {
          this.passkeyModalDismissed = false;
        }
      } catch (err) {
        if (
          !this.isUnmounted &&
          !this.passkeyBusy &&
          requestSequence === this.statusRequestSequence
        ) {
          this.status = 'ERROR';
        }
      } finally {
        this.loading = false;
      }
    },

    startStatusPolling() {
      this.stopStatusPolling();
      this.statusPollTimer = setInterval(() => this.fetchStatus(), 10000);
    },

    stopStatusPolling() {
      if (this.statusPollTimer) {
        clearInterval(this.statusPollTimer);
        this.statusPollTimer = null;
      }
    },

    async reconnect() {
      this.statusRequestSequence = (this.statusRequestSequence ?? 0) + 1;
      this.qrRequestSequence = (this.qrRequestSequence ?? 0) + 1;
      this.reconnecting = true;
      this.reconnectInProgress = true;
      this.qrError = null;
      this.qrCode = null;
      this.passkeyError = null;
      this.passkeyModalDismissed = false;
      try {
        await axios.post(
          `/api/v1/accounts/${this.accountId}/inboxes/${this.inbox.id}/waha/reconnect`
        );
        this.showQrModal = true;
        setTimeout(() => this.fetchQrCode(), 2000);
        this.startQrPolling();
      } catch (err) {
        this.qrError = this.$t('INBOX_MGMT.WAHA_STATUS.RECONNECT_ERROR', {
          error: err.response?.data?.error || err.message,
        });
        this.reconnectInProgress = false;
      } finally {
        this.reconnecting = false;
      }
    },

    async fetchHistoryImportStatus() {
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/inboxes/${this.inbox.id}/waha/import_history/status`
        );
        this.historyImportedAt = data.started_at || this.historyImportedAt;
        this.historyImportCommand =
          data.last_command || this.historyImportCommand;
      } catch (err) {
        // silent — non-critical
      }
    },

    async fetchSettings() {
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/inboxes/${this.inbox.id}/waha/settings`
        );
        this.settings = {
          show_agent_name: data.show_agent_name,
          mark_as_read: data.mark_as_read,
          enable_commands: data.enable_commands,
          locale: data.locale,
        };
        this.settingsSavedAt = data.last_synced_at;
        this.settingsLoaded = true;
      } catch (err) {
        this.settingsLoaded = true;
      }
    },

    async saveSetting(key, value) {
      const previous = this.settings[key];
      this.settings[key] = value;
      this.settingsSaving = true;
      this.settingsError = null;
      try {
        const { data } = await axios.patch(
          `/api/v1/accounts/${this.accountId}/inboxes/${this.inbox.id}/waha/settings`,
          { [key]: value }
        );
        this.settings = {
          ...this.settings,
          show_agent_name: data.show_agent_name,
          mark_as_read: data.mark_as_read,
          enable_commands: data.enable_commands,
          locale: data.locale,
        };
        this.settingsSavedAt = data.last_synced_at;
      } catch {
        this.settings[key] = previous;
        this.settingsError = this.$t(
          'INBOX_MGMT.WAHA_STATUS.SETTINGS_SAVE_ERROR'
        );
      } finally {
        this.settingsSaving = false;
      }
    },

    toggleSettingsPanel() {
      this.showSettings = !this.showSettings;
      if (this.showSettings && !this.settingsLoaded) {
        this.fetchSettings();
      }
    },

    openHistoryImportModal() {
      this.showHistoryImportModal = true;
    },

    closeHistoryImportModal() {
      this.showHistoryImportModal = false;
    },

    onHistoryImported(data) {
      this.historyImportedAt = data.started_at;
      this.historyImportCommand = data.command;
    },

    async fetchQrCode() {
      if (this.passkeyBusy) return;
      const requestSequence = (this.qrRequestSequence ?? 0) + 1;
      this.qrRequestSequence = requestSequence;
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/inboxes/${this.inbox.id}/waha/qr_code`
        );
        if (
          this.isUnmounted ||
          this.passkeyBusy ||
          requestSequence !== this.qrRequestSequence
        )
          return;
        this.statusRequestSequence = (this.statusRequestSequence ?? 0) + 1;
        this.qrCode = data.qr_code;
        if (data.status) this.status = data.status;
        if (data.status === 'WORKING') {
          this.closeQrModal();
          this.fetchStatus();
          this.maybePromptHistoryImport();
          return;
        }
        if (
          data.status === 'PASSKEY_REQUIRED' ||
          data.status === 'PASSKEY_CONFIRMATION_REQUIRED'
        ) {
          this.stopQrPolling();
          this.qrCode = null;
          this.qrError = null;
          if (!this.passkeyModalDismissed) {
            this.showQrModal = true;
            await this.pingPasskeyExtension();
            if (
              this.isUnmounted ||
              this.passkeyBusy ||
              requestSequence !== this.qrRequestSequence
            )
              return;
            if (data.status === 'PASSKEY_CONFIRMATION_REQUIRED') {
              await this.fetchPasskeyConfirmation();
            }
          }
        }
      } catch (err) {
        if (
          !this.isUnmounted &&
          !this.passkeyBusy &&
          requestSequence === this.qrRequestSequence
        ) {
          this.qrError = this.$t('INBOX_MGMT.WAHA_STATUS.QR_FETCH_ERROR');
        }
      }
    },

    // WAHA 2026.7 passkey flow — https://waha.devlike.pro/blog/waha-passkey/
    passkeyExtensionId() {
      return 'ghpdcgnjffaaekflfpcgkgpbafmjldcp';
    },
    isFirefoxBrowser() {
      return /firefox/i.test(navigator.userAgent);
    },
    async pingPasskeyExtension() {
      const TIMEOUT_MS = 400;
      try {
        if (this.isFirefoxBrowser()) {
          this.extensionReady = await this.callFirefoxPasskey(
            'waha-passkey-ping',
            null,
            TIMEOUT_MS
          )
            .then(() => true)
            .catch(() => false);
          return this.extensionReady;
        }
        if (!window.chrome?.runtime?.sendMessage) {
          this.extensionReady = false;
          return false;
        }
        this.extensionReady = await new Promise(resolve => {
          const timer = setTimeout(() => resolve(false), TIMEOUT_MS);
          window.chrome.runtime.sendMessage(
            this.passkeyExtensionId(),
            { type: 'waha-passkey-ping' },
            resp => {
              clearTimeout(timer);
              resolve(!window.chrome.runtime.lastError && resp?.ok === true);
            }
          );
        });
        return this.extensionReady;
      } catch (e) {
        this.extensionReady = false;
        return false;
      }
    },
    callFirefoxPasskey(type, challenge, timeoutMs = 120000) {
      return new Promise((resolve, reject) => {
        const requestId = crypto.randomUUID();
        let settled = false;
        let timer;
        let onMessage;
        let cancel;
        const cleanup = () => {
          clearTimeout(timer);
          window.removeEventListener('message', onMessage);
          if (this.passkeySignCleanup === cancel) {
            this.passkeySignCleanup = null;
          }
        };
        cancel = () => {
          if (settled) return;
          settled = true;
          cleanup();
          reject(new Error(PASSKEY_REQUEST_CANCELLED));
        };
        onMessage = event => {
          if (settled) return;
          if (event.source !== window) return;
          const data = event.data;
          if (
            data?.source !== 'waha-passkey-extension' ||
            data.requestId !== requestId
          )
            return;
          settled = true;
          cleanup();
          if (!data.ok) {
            reject(
              new Error(
                data.error ||
                  this.$t('INBOX_MGMT.WAHA_STATUS.PASSKEY_SIGN_FAILED')
              )
            );
            return;
          }
          resolve(data);
        };
        this.passkeySignCleanup = cancel;
        timer = setTimeout(() => {
          if (settled) return;
          settled = true;
          cleanup();
          reject(
            new Error(
              this.$t('INBOX_MGMT.WAHA_STATUS.PASSKEY_EXTENSION_TIMEOUT')
            )
          );
        }, timeoutMs);
        window.addEventListener('message', onMessage);
        window.postMessage(
          { source: 'waha-passkey-page', requestId, type, challenge },
          window.location.origin
        );
      });
    },
    signPasskeyChallenge(challenge) {
      if (this.isFirefoxBrowser()) {
        return this.callFirefoxPasskey('waha-passkey-sign', challenge).then(
          d => d.assertion
        );
      }
      return new Promise((resolve, reject) => {
        let settled = false;
        let timer = null;
        let cancel;
        const cleanup = () => {
          if (timer) clearTimeout(timer);
          timer = null;
          if (this.passkeySignCleanup === cancel) {
            this.passkeySignCleanup = null;
          }
        };
        cancel = () => {
          if (settled) return;
          settled = true;
          cleanup();
          reject(new Error(PASSKEY_REQUEST_CANCELLED));
        };
        timer = setTimeout(() => {
          if (settled) return;
          settled = true;
          cleanup();
          reject(
            new Error(
              this.$t('INBOX_MGMT.WAHA_STATUS.PASSKEY_EXTENSION_TIMEOUT')
            )
          );
        }, 120000);
        this.passkeySignCleanup = cancel;
        window.chrome.runtime.sendMessage(
          this.passkeyExtensionId(),
          { type: 'waha-passkey-sign', challenge },
          resp => {
            if (settled) return;
            settled = true;
            cleanup();
            if (window.chrome.runtime.lastError) {
              reject(new Error(window.chrome.runtime.lastError.message));
              return;
            }
            if (!resp?.ok) {
              reject(
                new Error(
                  resp?.error ||
                    this.$t('INBOX_MGMT.WAHA_STATUS.PASSKEY_SIGN_FAILED')
                )
              );
              return;
            }
            resolve(resp.assertion);
          }
        );
      });
    },
    async completePasskeyFlow() {
      let refreshStatus = false;
      this.statusRequestSequence = (this.statusRequestSequence ?? 0) + 1;
      this.qrRequestSequence = (this.qrRequestSequence ?? 0) + 1;
      this.passkeyBusy = true;
      this.passkeyError = null;
      try {
        const ready = await this.pingPasskeyExtension();
        if (this.isUnmounted) return;
        if (!ready) {
          this.passkeyError = this.$t(
            'INBOX_MGMT.WAHA_STATUS.PASSKEY_EXTENSION_MISSING'
          );
          return;
        }
        const { data: challenge } = await axios.get(
          `/api/v1/accounts/${this.accountId}/inboxes/${this.inbox.id}/waha/passkey/challenge`
        );
        if (this.isUnmounted) return;
        const assertion = await this.signPasskeyChallenge(challenge);
        if (this.isUnmounted) return;
        const { data } = await axios.post(
          `/api/v1/accounts/${this.accountId}/inboxes/${this.inbox.id}/waha/passkey`,
          { passkey: assertion }
        );
        if (this.isUnmounted) return;
        if (data.status) this.status = data.status;
        if (data.status === 'PASSKEY_CONFIRMATION_REQUIRED') {
          await this.fetchPasskeyConfirmation();
        } else if (data.status === 'WORKING') {
          this.passkeyModalDismissed = false;
          this.closeQrModal();
          refreshStatus = true;
          this.maybePromptHistoryImport();
        } else {
          refreshStatus = true;
        }
      } catch (err) {
        if (!this.isUnmounted) {
          this.passkeyError =
            err.response?.data?.error ||
            err.message ||
            this.$t('INBOX_MGMT.WAHA_STATUS.PASSKEY_COMPLETE_ERROR');
        }
      } finally {
        if (!this.isUnmounted) this.passkeyBusy = false;
      }
      if (refreshStatus && !this.isUnmounted) this.fetchStatus();
    },
    async fetchPasskeyConfirmation() {
      const statusRequestSequence = this.statusRequestSequence;
      const qrRequestSequence = this.qrRequestSequence;
      this.passkeyError = null;
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/inboxes/${this.inbox.id}/waha/passkey/confirmation`
        );
        if (
          this.isUnmounted ||
          statusRequestSequence !== this.statusRequestSequence ||
          qrRequestSequence !== this.qrRequestSequence
        )
          return;
        this.passkeyCode = data.code || null;
        if (data.status) this.status = data.status;
      } catch (err) {
        if (
          this.isUnmounted ||
          statusRequestSequence !== this.statusRequestSequence ||
          qrRequestSequence !== this.qrRequestSequence
        )
          return;
        this.passkeyCode = null;
        this.passkeyError =
          err.response?.data?.error ||
          err.message ||
          this.$t('INBOX_MGMT.WAHA_STATUS.PASSKEY_CONFIRMATION_ERROR');
      }
    },
    async confirmPasskeyCode() {
      if (!this.passkeyCode) return;
      let refreshStatus = false;
      this.statusRequestSequence = (this.statusRequestSequence ?? 0) + 1;
      this.qrRequestSequence = (this.qrRequestSequence ?? 0) + 1;
      this.passkeyBusy = true;
      this.passkeyError = null;
      try {
        const { data } = await axios.post(
          `/api/v1/accounts/${this.accountId}/inboxes/${this.inbox.id}/waha/passkey/confirm`
        );
        if (this.isUnmounted) return;
        if (data.status) this.status = data.status;
        if (data.status === 'WORKING') {
          this.passkeyModalDismissed = false;
          this.closeQrModal();
          refreshStatus = true;
          this.maybePromptHistoryImport();
        }
      } catch (err) {
        if (!this.isUnmounted) {
          this.passkeyError =
            err.response?.data?.error ||
            err.message ||
            this.$t('INBOX_MGMT.WAHA_STATUS.PASSKEY_CONFIRM_ERROR');
        }
      } finally {
        if (!this.isUnmounted) this.passkeyBusy = false;
      }
      if (refreshStatus && !this.isUnmounted) this.fetchStatus();
    },

    async openPasskeyModal() {
      this.passkeyModalDismissed = false;
      this.passkeyError = null;
      this.qrError = null;
      this.showQrModal = true;
      await this.pingPasskeyExtension();
      if (this.status === 'PASSKEY_CONFIRMATION_REQUIRED') {
        await this.fetchPasskeyConfirmation();
      }
    },

    async retryPasskeyAfterError() {
      this.passkeyError = null;
      if (this.status === 'PASSKEY_CONFIRMATION_REQUIRED') {
        // Prefer re-confirming on phone; if code is missing, reload it first.
        if (!this.passkeyCode) {
          await this.fetchPasskeyConfirmation();
          return;
        }
        await this.confirmPasskeyCode();
        return;
      }
      await this.completePasskeyFlow();
    },

    // Opt-B "nudge" — fire the history import modal automatically right after
    // a successful QR scan, but only once per inbox. If the user ever ran an
    // import in the past (historyImportedAt is set), we stay quiet.
    maybePromptHistoryImport() {
      if (!this.reconnectInProgress) return;
      this.reconnectInProgress = false;
      if (this.historyImportedAt) return;

      // Small delay so the "Reconectado!" success state on the QR modal has
      // time to show before we switch to the import modal.
      setTimeout(() => {
        this.showHistoryImportModal = true;
      }, 1200);
    },

    startQrPolling() {
      this.stopQrPolling();
      this.qrPollTimer = setInterval(() => this.fetchQrCode(), 3000);
    },

    stopQrPolling() {
      if (this.qrPollTimer) {
        clearInterval(this.qrPollTimer);
        this.qrPollTimer = null;
      }
    },

    closeQrModal() {
      if (
        this.status === 'PASSKEY_REQUIRED' ||
        this.status === 'PASSKEY_CONFIRMATION_REQUIRED'
      ) {
        this.passkeyModalDismissed = true;
      }
      this.showQrModal = false;
      this.stopQrPolling();
    },
  },
};
</script>

<template>
  <div
    class="rounded-2xl border border-n-weak bg-n-surface-1 p-4 flex flex-col gap-3"
  >
    <!-- Header -->
    <div class="flex items-center justify-between gap-3">
      <div class="flex items-center gap-3">
        <div
          class="size-10 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center"
        >
          <span
            class="i-ri-message-3-line size-5 text-teal-600 dark:text-teal-400"
          />
        </div>
        <div>
          <h3 class="text-sm font-bold text-n-slate-12 m-0">
            {{ $t('INBOX_MGMT.WAHA_STATUS.TITLE') }}
          </h3>
          <p class="text-xs text-n-slate-11">
            {{ $t('INBOX_MGMT.WAHA_STATUS.SESSION') }}
            <span class="font-mono">{{ sessionName }}</span>
          </p>
        </div>
      </div>

      <div class="flex items-center gap-2">
        <div
          class="flex items-center gap-2 px-3 py-1.5 rounded-full border border-n-weak"
        >
          <div
            class="size-2.5 rounded-full animate-pulse"
            :class="meta.color"
          />
          <span class="text-xs font-semibold" :class="meta.textColor">{{
            meta.label
          }}</span>
        </div>
      </div>
    </div>

    <!-- Details -->
    <div
      class="grid grid-cols-2 gap-2 text-xs text-n-slate-11 pt-2 border-t border-n-weak"
    >
      <div>
        <span class="text-n-slate-11">{{
          $t('INBOX_MGMT.WAHA_STATUS.ENGINE')
        }}</span>
        <span class="ml-1 font-semibold text-n-slate-12">{{ engine }}</span>
      </div>
      <div>
        <span class="text-n-slate-11">{{
          $t('INBOX_MGMT.WAHA_STATUS.LAST_CHECK')
        }}</span>
        <span class="ml-1 text-n-slate-12">{{ checkedAtLabel }}</span>
      </div>
      <div v-if="historyImportedLabel" class="col-span-2">
        <span class="text-n-slate-11">{{
          $t('INBOX_MGMT.WAHA_STATUS.LAST_HISTORY_IMPORT')
        }}</span>
        <span class="ml-1 text-n-slate-12">{{ historyImportedLabel }}</span>
      </div>
    </div>

    <!-- Actions -->
    <div class="flex flex-wrap gap-2 pt-2">
      <button
        class="px-3 py-1.5 rounded-xl text-xs font-medium border border-n-weak text-n-slate-12 hover:bg-n-slate-2 dark:hover:bg-n-solid-2 disabled:opacity-50"
        :disabled="loading"
        @click="fetchStatus"
      >
        <span
          :class="
            loading
              ? 'i-ri-loader-4-line animate-spin'
              : 'i-ri-refresh-line'
          "
          class="size-3 inline-block mr-1"
        />
        {{ $t('INBOX_MGMT.WAHA_STATUS.REFRESH') }}
      </button>
      <button
        v-if="showReconnect"
        class="px-3 py-1.5 rounded-xl text-xs font-semibold bg-n-blue-9 text-white hover:bg-n-blue-10 disabled:opacity-50 flex items-center gap-1"
        :disabled="reconnecting"
        @click="reconnect"
      >
        <span
          :class="
            reconnecting
              ? 'i-ri-loader-4-line animate-spin'
              : 'i-ri-anticlockwise-line'
          "
          class="size-3"
        />
        {{
          reconnecting
            ? $t('INBOX_MGMT.WAHA_STATUS.RECONNECTING')
            : $t('INBOX_MGMT.WAHA_STATUS.RECONNECT')
        }}
      </button>
      <button
        v-if="showImportHistory"
        class="px-3 py-1.5 rounded-xl text-xs font-semibold border border-teal-500 text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 flex items-center gap-1"
        @click="openHistoryImportModal"
      >
        <span class="i-ri-history-line size-3" />
        {{ $t('INBOX_MGMT.WAHA_STATUS.IMPORT_HISTORY') }}
      </button>
      <button
        v-if="showContinuePasskey"
        class="px-3 py-1.5 rounded-xl text-xs font-semibold bg-violet-600 text-white hover:bg-violet-700 flex items-center gap-1"
        @click="openPasskeyModal"
      >
        <span class="i-ri-key-2-line size-3" />
        {{ $t('INBOX_MGMT.WAHA_STATUS.CONTINUE_PASSKEY') }}
      </button>
    </div>

    <!-- Configurações WAHA sub-card (collapsible) -->
    <div class="border border-n-weak rounded-xl mt-1 overflow-hidden">
      <button
        class="flex w-full items-center justify-between gap-2 px-3 py-2 text-xs font-medium text-n-slate-12 hover:bg-n-slate-2 dark:hover:bg-n-solid-2"
        @click="toggleSettingsPanel"
      >
        <span class="flex items-center gap-2">
          <span class="i-ri-equalizer-line size-3.5 text-n-slate-11" />
          {{ $t('INBOX_MGMT.WAHA_STATUS.SETTINGS') }}
        </span>
        <span
          :class="
            showSettings ? 'i-ri-arrow-up-s-line' : 'i-ri-arrow-down-s-line'
          "
          class="size-3 text-n-slate-11"
        />
      </button>
      <div
        v-if="showSettings"
        class="border-t border-n-weak px-3 py-3 flex flex-col gap-3"
      >
        <div v-if="!settingsLoaded" class="text-xs text-n-slate-11">
          <span
            class="i-ri-loader-4-line animate-spin size-3 inline-block mr-1"
          />
          {{ $t('INBOX_MGMT.WAHA_STATUS.LOADING') }}
        </div>
        <template v-else>
          <div
            v-if="settingsError"
            role="alert"
            class="rounded-md bg-red-50 px-2 py-1.5 text-[11px] text-red-700 dark:bg-red-950/30 dark:text-red-300"
          >
            {{ settingsError }}
          </div>

          <!-- Show agent name -->
          <label class="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              :checked="settings.show_agent_name"
              :disabled="settingsSaving"
              @change="saveSetting('show_agent_name', $event.target.checked)"
            />
            <div class="flex-1">
              <span class="text-xs font-medium text-n-slate-12">
                {{ $t('INBOX_MGMT.WAHA_STATUS.SHOW_AGENT_NAME') }}
              </span>
              <span class="block text-[11px] text-n-slate-11">
                {{ $t('INBOX_MGMT.WAHA_STATUS.SHOW_AGENT_NAME_HINT') }}
              </span>
            </div>
          </label>

          <!-- Mark as read -->
          <label class="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              :checked="settings.mark_as_read"
              :disabled="settingsSaving"
              @change="saveSetting('mark_as_read', $event.target.checked)"
            />
            <div class="flex-1">
              <span class="text-xs font-medium text-n-slate-12">
                {{ $t('INBOX_MGMT.WAHA_STATUS.MARK_AS_READ') }}
              </span>
              <span class="block text-[11px] text-n-slate-11">
                {{ $t('INBOX_MGMT.WAHA_STATUS.MARK_AS_READ_HINT') }}
              </span>
            </div>
          </label>

          <!-- Enable commands -->
          <label class="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              :checked="settings.enable_commands"
              :disabled="settingsSaving"
              @change="saveSetting('enable_commands', $event.target.checked)"
            />
            <div class="flex-1">
              <span class="text-xs font-medium text-n-slate-12">
                {{ $t('INBOX_MGMT.WAHA_STATUS.COMMANDS') }}
              </span>
              <span class="block text-[11px] text-n-slate-11">
                {{ $t('INBOX_MGMT.WAHA_STATUS.COMMANDS_HINT') }}
              </span>
            </div>
          </label>

          <!-- Locale -->
          <div class="flex items-center gap-2">
            <span class="text-xs font-medium text-n-slate-12 flex-1">
              {{ $t('INBOX_MGMT.WAHA_STATUS.PLUGIN_LOCALE') }}
            </span>
            <select
              :value="settings.locale"
              :disabled="settingsSaving"
              class="text-xs border border-n-weak rounded px-2 py-1 bg-n-surface-1 text-n-slate-12"
              @change="saveSetting('locale', $event.target.value)"
            >
              <option value="pt-BR">
                {{ $t('INBOX_MGMT.WAHA_STATUS.LOCALE_PT_BR') }}
              </option>
              <option value="en">
                {{ $t('INBOX_MGMT.WAHA_STATUS.LOCALE_EN') }}
              </option>
              <option value="es">
                {{ $t('INBOX_MGMT.WAHA_STATUS.LOCALE_ES') }}
              </option>
            </select>
          </div>

          <div
            v-if="settingsSavedAt"
            class="text-[10px] text-n-slate-11 border-t border-n-weak pt-2"
          >
            <span class="i-ri-checkbox-circle-line size-3 inline-block mr-1" />
            {{
              $t('INBOX_MGMT.WAHA_STATUS.SYNCED_AT', {
                date: new Date(settingsSavedAt).toLocaleString('pt-BR'),
              })
            }}
          </div>
        </template>
      </div>
    </div>

    <WahaHistoryImportModal
      :inbox="inbox"
      :show="showHistoryImportModal"
      @close="closeHistoryImportModal"
      @imported="onHistoryImported"
    />

    <!-- QR / passkey modal -->
    <div
      v-if="showQrModal"
      class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="waha-qr-modal-title"
      @click.self="closeQrModal"
    >
      <div
        class="bg-n-surface-1 border border-n-weak rounded-2xl p-6 max-w-md w-full"
      >
        <div class="flex items-center justify-between mb-4">
          <h3
            id="waha-qr-modal-title"
            class="text-lg font-extrabold text-n-slate-12"
          >
            {{ $t('INBOX_MGMT.WAHA_STATUS.MODAL_TITLE') }}
          </h3>
          <button
            type="button"
            class="size-8 rounded-xl text-n-slate-11 hover:bg-n-slate-3 dark:hover:bg-n-solid-3 flex items-center justify-center"
            :aria-label="$t('INBOX_MGMT.WAHA_STATUS.CLOSE_MODAL')"
            @click="closeQrModal"
          >
            <span class="i-ri-close-line size-5" />
          </button>
        </div>

        <p
          v-if="
            status === 'PASSKEY_REQUIRED' ||
            status === 'PASSKEY_CONFIRMATION_REQUIRED'
          "
          class="text-sm text-n-slate-11 mb-4"
        >
          {{ $t('INBOX_MGMT.WAHA_STATUS.PASSKEY_INTRO') }}
        </p>
        <p v-else class="text-sm text-n-slate-11 mb-4">
          {{ $t('INBOX_MGMT.WAHA_STATUS.QR_INTRO') }}
        </p>

        <div class="flex flex-col items-center justify-center min-h-[300px]">
          <div
            v-if="qrError || passkeyError"
            class="text-center px-2"
            role="alert"
            aria-live="assertive"
          >
            <span
              class="i-ri-wifi-off-line size-12 text-red-500 block mx-auto mb-3"
            />
            <p class="text-sm text-red-600 dark:text-red-400">
              {{ passkeyError || qrError }}
            </p>
            <a
              v-if="
                !extensionReady &&
                (status === 'PASSKEY_REQUIRED' ||
                  status === 'PASSKEY_CONFIRMATION_REQUIRED')
              "
              class="inline-block mt-3 text-sm text-n-blue-9 hover:underline"
              :href="passkeyExtensionStoreUrl"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ $t('INBOX_MGMT.WAHA_STATUS.INSTALL_EXTENSION') }}
            </a>
            <button
              v-if="
                passkeyError &&
                (status === 'PASSKEY_REQUIRED' ||
                  status === 'PASSKEY_CONFIRMATION_REQUIRED')
              "
              type="button"
              class="mt-4 px-4 py-2 rounded-xl bg-violet-600 text-white text-sm font-semibold disabled:opacity-50"
              :disabled="passkeyBusy"
              @click="retryPasskeyAfterError"
            >
              {{
                passkeyBusy
                  ? $t('INBOX_MGMT.WAHA_STATUS.WAITING')
                  : $t('INBOX_MGMT.WAHA_STATUS.RETRY_PASSKEY')
              }}
            </button>
          </div>
          <div v-else-if="status === 'WORKING'" class="text-center">
            <div
              class="size-20 rounded-full bg-green-500 flex items-center justify-center mx-auto mb-3"
            >
              <span class="i-ri-check-line size-12 text-white" />
            </div>
            <p class="text-lg font-extrabold text-green-600 dark:text-green-400">
              {{ $t('INBOX_MGMT.WAHA_STATUS.RECONNECTED') }}
            </p>
          </div>
          <div
            v-else-if="status === 'PASSKEY_CONFIRMATION_REQUIRED'"
            class="text-center w-full px-2"
          >
            <p class="text-sm text-n-slate-11 mb-2">
              {{ $t('INBOX_MGMT.WAHA_STATUS.CONFIRMATION_PROMPT') }}
            </p>
            <p
              class="text-4xl font-mono font-bold tracking-widest text-n-slate-12 mb-4"
            >
              {{ passkeyCode || '····' }}
            </p>
            <button
              type="button"
              class="px-4 py-2 rounded-xl bg-n-blue-9 text-white text-sm font-semibold disabled:opacity-50"
              :disabled="passkeyBusy || !passkeyCode"
              @click="confirmPasskeyCode"
            >
              {{
                passkeyBusy
                  ? $t('INBOX_MGMT.WAHA_STATUS.CONFIRMING')
                  : $t('INBOX_MGMT.WAHA_STATUS.CONFIRMED_ON_PHONE')
              }}
            </button>
          </div>
          <div
            v-else-if="status === 'PASSKEY_REQUIRED'"
            class="text-center w-full px-2"
          >
            <span
              class="i-ri-key-2-line size-12 text-violet-500 block mx-auto mb-3"
            />
            <p class="text-sm text-n-slate-11 mb-4">
              {{ $t('INBOX_MGMT.WAHA_STATUS.PASSKEY_PROMPT') }}
            </p>
            <button
              type="button"
              class="px-4 py-2 rounded-xl bg-violet-600 text-white text-sm font-semibold disabled:opacity-50"
              :disabled="passkeyBusy"
              @click="completePasskeyFlow"
            >
              {{
                passkeyBusy
                  ? $t('INBOX_MGMT.WAHA_STATUS.WAITING')
                  : $t('INBOX_MGMT.WAHA_STATUS.CONTINUE_PASSKEY')
              }}
            </button>
            <p class="text-xs text-n-slate-11 mt-4">
              {{ $t('INBOX_MGMT.WAHA_STATUS.FALLBACK_PREFIX') }}
              <!-- eslint-disable vue/html-closing-bracket-newline -->
              <a
                class="text-n-blue-9 hover:underline"
                href="https://waha.devlike.pro/blog/waha-passkey/"
                target="_blank"
                rel="noopener noreferrer"
                >{{ $t('INBOX_MGMT.WAHA_STATUS.WAHA_GUIDE') }}</a
              >
              <!-- eslint-enable vue/html-closing-bracket-newline -->
            </p>
          </div>
          <div v-else-if="!qrCode" class="text-center">
            <span
              class="i-ri-loader-4-line size-12 text-n-blue-9 animate-spin block mx-auto mb-3"
            />
            <p class="text-sm text-n-slate-11">
              {{ $t('INBOX_MGMT.WAHA_STATUS.GENERATING_QR') }}
            </p>
          </div>
          <img
            v-else
            :src="qrCode"
            :alt="$t('INBOX_MGMT.WAHA_STATUS.QR_ALT')"
            class="w-64 h-64 rounded-2xl border-4 border-n-weak"
          />
        </div>

        <div
          class="flex items-center justify-between mt-4 pt-3 border-t border-n-weak text-xs text-n-slate-11"
        >
          <div class="flex items-center gap-2">
            <div
              class="size-2.5 rounded-full animate-pulse"
              :class="meta.color"
            />
            <span :class="meta.textColor">{{ meta.label }}</span>
          </div>
          <button
            v-if="
              qrCode &&
              status !== 'WORKING' &&
              status !== 'PASSKEY_REQUIRED' &&
              status !== 'PASSKEY_CONFIRMATION_REQUIRED'
            "
            class="text-n-blue-9 hover:underline"
            @click="fetchQrCode"
          >
            {{ $t('INBOX_MGMT.WAHA_STATUS.UPDATE_QR') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
